import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {SelectItem} from '@components/inputs/select/Select.component';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import ModalComponent from '@components/modal/Modal.component';
import {Colors} from '@constants/colors.constants';
import {Sizes} from '@constants/sizes.constants';
import {LoadingButton} from '@mui/lab';
import {Button, Chip, LinearProgress, Stack, Typography} from '@mui/material';
import ClientsServicesTable from '@screens/admin/clients/services/ClientsServices.table';
import {clientsServicesQuery, clientsServicesService} from '@store/clients/services';
import {indexationService} from '@store/settings/indexation';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {fromEvent, map} from 'rxjs';
import {useSnackbar} from 'notistack';
import dayjs from 'dayjs';
import * as io from 'socket.io-client';
import env from '@config/env.config';

interface UpdateServicesModalProps {
  handleClose: () => void;
}


const UpdateServicesModal = (props: UpdateServicesModalProps) => {

  const {handleClose} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [saveLoading, setSaveLoading] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const [step, setStep] = useState<number>(0);
  const [showDate, setShowDate] = useState(false);
  const [showBooleans, setShowBooleans] = useState(false);
  const [showIndexations, setShowIndexations] = useState(false);

  const [serviceUpdate, setServiceUpdate] = useState<{
    validToDate?: Date;
    sellIndexation?: SelectItem;
    buyIndexation?: SelectItem;
    canBeRequested?: boolean;
    interco?: boolean;
    intersite?: boolean;
    clientInvoicingRequired?: boolean;
    supplierInvoicingRequired?: boolean;
  }>({});

  const [tempCanbeRequested, setTempCanbeRequested] = useState<boolean | undefined>(serviceUpdate.canBeRequested);
  const [tempClientInvoicingRequired, setTempClientInvoicingRequired] = useState<boolean | undefined>(serviceUpdate.clientInvoicingRequired);
  const [tempSupplierInvoicingRequired, setTempSupplierInvoicingRequired] = useState<boolean | undefined>(serviceUpdate.supplierInvoicingRequired);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack pt="20px" pb="20px" height="85vh" width="100%" direction="column" spacing={2} alignItems="center">
            <Typography>
              {t('clients.details.contractedServices.updatePrices.step1Description')}
            </Typography>
            <Stack flex={1} width="100%" spacing={2}>
              <ClientsServicesTable enableActions={false}/>
            </Stack>
          </Stack>
        );
      case 1:
        return (
          <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={2} alignItems="center">
            <Stack flex={1} width="100%" spacing={5} alignItems="center">
              <Stack spacing={1} width="70%">
                <CheckboxComponent
                  label={t('clients.details.contractedServices.updates.updateDate')}
                  value={showDate}
                  labelPlacement="end"
                  handleChange={(checked) => setShowDate(checked)}
                  labelWidth={Sizes.drawerInputLabelWidth}
                />
                {showDate && <DatePickerComponent
                    placeholder={t('global.selectDate')}
                    label={t('clients.details.contractedServices.manage.validityEnd')}
                    value={serviceUpdate.validToDate}
                    labelWidth={Sizes.drawerInputLabelWidth}
                    color="secondary"
                    handleChange={(validToDate) =>
                      setServiceUpdate({
                        ...serviceUpdate,
                        validToDate: validToDate,
                      })
                    }/>}
              </Stack>

              <Stack spacing={1} width="70%">
                <CheckboxComponent
                  label={t('clients.details.contractedServices.updates.updateBooleans')}
                  labelWidth={Sizes.drawerInputLabelWidth}
                  labelPlacement="end"
                  value={showBooleans}
                  handleChange={(checked) => setShowBooleans(checked)}/>
                {showBooleans && <Stack direction="column" spacing={2} useFlexGap flexWrap="wrap">
                    <Stack spacing={1}>
                        <Typography>{t('clients.details.contractedServices.manage.canBeRequested')}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Chip
                                label={t('clients.details.contractedServices.updates.yes')}
                                style={{
                                  backgroundColor: tempCanbeRequested ? '#6FD969' : '#FFFFFF',
                                  color: tempCanbeRequested ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempCanbeRequested(true)}
                            />
                            <Chip
                                label={t('clients.details.contractedServices.updates.no')}
                                style={{
                                  backgroundColor: tempCanbeRequested === false ? Colors.lightRed : '#FFFFFF',
                                  color: tempCanbeRequested == false ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempCanbeRequested(false)}
                            />
                            <Chip
                                label={t('clients.details.contractedServices.updates.noChange')}
                                style={{
                                  backgroundColor: tempCanbeRequested == undefined ? Colors.lightGrey : '#FFFFFF',
                                  color: tempCanbeRequested == undefined ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempCanbeRequested(undefined)}/>
                        </Stack>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t('clients.details.contractedServices.manage.clientInvoicingRequired')}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Chip
                                label={t('clients.details.contractedServices.updates.yes')}
                                style={{
                                  backgroundColor: tempClientInvoicingRequired ? '#6FD969' : '#FFFFFF',
                                  color: tempClientInvoicingRequired ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempClientInvoicingRequired(true)}
                            />
                            <Chip
                                label={t('clients.details.contractedServices.updates.no')}
                                style={{
                                  backgroundColor: tempClientInvoicingRequired == false ? Colors.lightRed : '#FFFFFF',
                                  color: tempClientInvoicingRequired == false ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempClientInvoicingRequired(false)}
                            />
                            <Chip
                                label={t('clients.details.contractedServices.updates.noChange')}
                                style={{
                                  backgroundColor: tempClientInvoicingRequired == undefined ? Colors.lightGrey : '#FFFFFF',
                                  color: tempClientInvoicingRequired == undefined ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempClientInvoicingRequired(undefined)}/>
                        </Stack>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography>{t('clients.details.contractedServices.manage.supplierInvoicingRequired')}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Chip
                                label={t('clients.details.contractedServices.updates.yes')}
                                style={{
                                  backgroundColor: tempSupplierInvoicingRequired ? '#6FD969' : '#FFFFFF',
                                  color: tempSupplierInvoicingRequired ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempSupplierInvoicingRequired(true)}
                            />
                            <Chip
                                label={t('clients.details.contractedServices.updates.no')}
                                style={{
                                  backgroundColor: tempSupplierInvoicingRequired == false ? Colors.lightRed : '#FFFFFF',
                                  color: tempSupplierInvoicingRequired == false ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempSupplierInvoicingRequired(false)}
                            />

                            <Chip
                                label={t('clients.details.contractedServices.updates.noChange')}
                                style={{
                                  backgroundColor: tempSupplierInvoicingRequired == undefined ? Colors.lightGrey : '#FFFFFF',
                                  color: tempSupplierInvoicingRequired == undefined ? '#192133' : '#868F9C',
                                }}
                                onClick={() => setTempSupplierInvoicingRequired(undefined)}/>
                        </Stack>
                    </Stack>
                </Stack>}
              </Stack>

              <Stack spacing={1} width="70%">
                <CheckboxComponent
                  label={t('clients.details.contractedServices.updates.updateIndexations')}
                  labelWidth={Sizes.drawerInputLabelWidth}
                  labelPlacement="end"
                  value={showIndexations}
                  handleChange={(checked) => setShowIndexations(checked)}/>
                {showIndexations && <Stack direction="column" spacing={2}>
                    <AsyncSelectComponent
                        color="secondary"
                        getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
                          value: indexation.id,
                          label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`,
                        }))))}
                        label={t('clients.details.contractedServices.manage.buyIndexation')}
                        labelWidth={Sizes.drawerInputLabelWidth}
                        handleChange={(indexation) => setServiceUpdate({
                          ...serviceUpdate,
                          buyIndexation: indexation ?? undefined
                        })}
                        placeholder={t('global.select')}
                        value={serviceUpdate.buyIndexation}

                    />
                    <AsyncSelectComponent
                        color="secondary"
                        getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
                          value: indexation.id,
                          label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`,
                        }))))}
                        label={t('clients.details.contractedServices.manage.sellIndexation')}
                        labelWidth={Sizes.drawerInputLabelWidth}
                        handleChange={(indexation) => setServiceUpdate({
                          ...serviceUpdate,
                          sellIndexation: indexation ?? undefined
                        })}
                        placeholder={t('global.select')}
                        value={serviceUpdate.sellIndexation}

                    />
                </Stack>}
              </Stack>
            </Stack>
          </Stack>
        );
      case 2:
        return (
          <Stack pt="20px" pb="20px" width="100%" direction="column" spacing={3} alignItems="center">
            {progress === 0 &&
                <Typography>{t('clients.details.contractedServices.updates.lastStepWarning', {count: clientsServicesQuery.totalRows})}</Typography>}
            {progress > 0 && <>
                <Typography>{t('clients.details.contractedServices.updatePrices.updateInProgress')}</Typography>
                <LinearProgress sx={{width: '100%'}} variant="determinate"
                                value={progress / clientsServicesQuery.totalRows * 100}/>
                <Typography>{progress} / {clientsServicesQuery.totalRows}</Typography>
            </>}
          </Stack>
        );
    }
  };

  const handleSave = () => {
    setSaveLoading(true);
    const token = Math.random().toString(16).slice(2);
    const ioClient = io.connect(`${env.API_URL}${env.API_URL.endsWith('/') ? '' : '/'}ws`, {
      path: '/api/ws/socket.io',
    });
    const payload = {
      ...(showDate ? {validToDate: serviceUpdate.validToDate ? dayjs(serviceUpdate.validToDate).set('hour', 12).toDate() : null} : {}),
      ...(showIndexations ? {
        buyIndexationId: serviceUpdate.buyIndexation?.value ?? null,
        sellIndexationId: serviceUpdate.sellIndexation?.value ?? null,
      } : {}),
      ...(showBooleans ? {
        canBeRequested: tempCanbeRequested,
        clientInvoicingRequired: tempClientInvoicingRequired,
        supplierInvoicingRequired: tempSupplierInvoicingRequired,
      } : {}),
    };
    clientsServicesService.updateServicesSettings(payload, token).subscribe({
      next: () => {
        enqueueSnackbar(t('clients.details.contractedServices.updates.success'), {variant: 'success'});
        handleClose();
        setSaveLoading(false);
        ioClient.disconnect();
      },
      error: (err: any) => {
        enqueueSnackbar(err.text, err.options);
        setSaveLoading(false);
        ioClient.disconnect();
      },
    });

    ioClient.on('connect', () => {
      fromEvent(ioClient, `progress-${token}`).subscribe((data: any) => {
        setProgress(data?.[0]);
      });
    });
  };

  const getStepAction = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Button variant="contained" color="primary" onClick={() => setStep(1)}>
            {t('global.next')}
          </Button>
        );
      case 1:
        return (
          <Button variant="contained" color="primary" onClick={() => setStep(2)}>
            {t('global.next')}
          </Button>
        );
      case 2:
        return (
          <LoadingButton loading={saveLoading} variant="contained" color="primary" onClick={handleSave}>
            {t('global.save')}
          </LoadingButton>
        );
    }
  };


  return (<ModalComponent
    aria-label="over-drawer"
    handleClose={handleClose}
    maxWidth={step === 0 ? false : 'lg'}
    title={t('clients.details.contractedServices.updates.title')}
    content={
      getStepContent(step)
    }
    actions={(
      <Stack direction="row" spacing={2}>
        {step === 0 && <Button variant="outlined" color="primary" onClick={handleClose}>{t('global.cancel')}</Button>}
        {step !== 0 && <Button variant="outlined" color="primary"
                               onClick={() => setStep(step - 1)}>{t('global.previous')}</Button>}
        {getStepAction(step)}
      </Stack>
    )}
  />);
};

export default UpdateServicesModal;
